import { Box, Grid } from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import { Typography } from '@uhc-tempo/components';
import React from 'react';
import moment from 'moment';
import camelcase from 'lodash/camelCase';
import toLower from 'lodash/toLower';
import upperFirst from 'lodash/upperFirst';
import CostProgressBar from './CostProgressBar.component';
import PopUpJSX from './PopUpToolTip.component';
import {
    INDIVIDUAL_DEDUCTIBLE,
    FAMILY_DEDUCTIBLE,
    INDIVIDUAL_OOP,
    FAMILY_OOP,
    OUT_NETWORK_TIER,
    SOURCE_CODE_IFP,
    DENTAL_PLAN_MAX,
    FAMILY_DENTAL_PLAN_MAX,
} from '../../../constants/index';

//Tooltip contents as string values
export const toolTipDeductible = `The amount you could owe during a coverage period (usually one year) for health care services your health insurance or plan covers before your health insurance or plan begins to pay.\n

        For example, if your deductible is $1000, your plan won’t pay anything until you’ve met your $1000 deductible for covered health care services subject to the deductible. The deductible may not apply to all services.`;

export const toolTipPocket = `The most money you have to pay for covered expenses in a plan year.`;

const toolTipDentalPlanMaximum =
    'The maximum amount the plan will pay for dental services for a member in current plan year. Claims for dental services not covered, such as cosmetic, do not count towards the annual maximum.';

const invalidAccumulatorValues = ['999999999.99', '999999', '999999.99'];

// When valid accumulators aren't found for individual or family Deds or OOP, the cost bars aren't displayed
export const checkIfValidAccumulatorData = (tierData, accumulatorType) => {
    if (
        !tierData?.[accumulatorType] ||
        !+tierData[accumulatorType]?.yearlyMax || // checks falsy and '0'
        !tierData[accumulatorType]?.remaining ||
        invalidAccumulatorValues.includes(tierData[accumulatorType]?.remaining) ||
        invalidAccumulatorValues.includes(tierData[accumulatorType]?.yearlyMax)
    ) {
        return false;
    }
    return true;
};

// TODO: When CBS & Cost Calculator return an indicator (yes or no) for coverage, we should switch to using that logic.
export const checkOonCoverage = (estimateResponse) => {
    let oonIndex = estimateResponse?.accumulators?.findIndex(
        (accumulator) => accumulator.tier === OUT_NETWORK_TIER
    );
    if (oonIndex > -1 && estimateResponse?.populationConfig?.sourceCode != SOURCE_CODE_IFP) {
        let oonAccums = estimateResponse?.accumulators?.[oonIndex];
        return (
            checkIfValidAccumulatorData(oonAccums, INDIVIDUAL_OOP) ||
            checkIfValidAccumulatorData(oonAccums, FAMILY_OOP) ||
            checkIfValidAccumulatorData(oonAccums, INDIVIDUAL_DEDUCTIBLE) ||
            checkIfValidAccumulatorData(oonAccums, FAMILY_DEDUCTIBLE)
        );
    } else {
        return false;
    }
};

// Plan Summary section
export const renderPlanSummaryAccumulators = (
    renderInNetwork,
    estimateResponse,
    isDesktopView,
    firstName,
    printFullPlanSummary
) => {
    let accumulatorsToRender = null;
    if (
        renderInNetwork ||
        (estimateResponse?.accumulators?.length > 0 &&
            (estimateResponse?.noInnGACPACRates?.hasBenefits ||
                estimateResponse.indemnityNoResponse))
    ) {
        // We want to get all the accumulators except the out-of-network one
        accumulatorsToRender = estimateResponse?.accumulators?.filter(
            (cost) => cost.tier != OUT_NETWORK_TIER
        );
    } else {
        // We want to get ONLY the out-of-network accumulators
        accumulatorsToRender = estimateResponse?.accumulators?.filter(
            (cost) => cost.tier === OUT_NETWORK_TIER
        );
    }
    return (
        <>
            {accumulatorsToRender && accumulatorsToRender.length > 0 && (
                <>
                    <Grid
                        container
                        direction="row"
                        alignItems="flex-end"
                        sx={{ paddingBottom: isDesktopView ? '1.2em' : '0.7em' }}
                    >
                        <Typography.H4
                            style={{
                                marginBottom: 0,
                                paddingRight: 4,
                                color: '#333333',
                            }}
                        >
                            Your current plan summary
                        </Typography.H4>
                        {isDesktopView && (
                            <Typography.Paragraph
                                style={{ marginBottom: 0, paddingBottom: '0.22em' }}
                            >
                                as of {moment().format('MMM D, YYYY')}
                            </Typography.Paragraph>
                        )}
                    </Grid>
                    <Grid
                        sx={
                            printFullPlanSummary
                                ? { overflowY: 'visible' }
                                : { overflowY: 'auto', height: '25em' }
                        }
                    >
                        {accumulatorsToRender.map((tier, index) => {
                            return (
                                <AccumulatorBarsPerTier
                                    key={index}
                                    tierData={tier}
                                    firstName={firstName}
                                    index={index}
                                />
                            );
                        })}
                    </Grid>
                </>
            )}
        </>
    );
};

// Render when out-of-network estimate and hasOonCoverage is false
export const renderNoOonCoverageDisclaimer = () => {
    return (
        <Grid container direction="row" alignItems="flex-end">
            <Box
                component="div"
                my={3}
                px={3}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#E5F8FB',
                    padding: '1rem',
                }}
            >
                <Box>
                    <Grid item sx={{ display: 'flex' }}>
                        <FlagIcon style={{ color: '#002677' }} />{' '}
                        <Typography.Paragraph
                            style={{
                                fontWeight: 700,
                                marginLeft: '1rem',
                            }}
                        >
                            Out-of-Network services are not covered by your plan. <br />
                            Your plan does not pay any portion of Out-of-Network services.
                        </Typography.Paragraph>
                    </Grid>
                    <Grid item>
                        <Typography.Paragraph>
                            If you are treated by an out of-network provider, you may still get a
                            bill for the difference between the amount the out-of-network provider
                            charges for an item or service and the amount your plan paid for that
                            same item or service. This is called <b>balance billing</b>, and this
                            amount is not included in your estimate.
                        </Typography.Paragraph>
                    </Grid>
                </Box>
            </Box>
        </Grid>
    );
};

/**
 * Renders up to 4 progress bars per Tier.
 * Tooltips are only rendered for the first tier and are for individual OOP and individual Deductible only
 */
export const AccumulatorBarsPerTier = ({ tierData, firstName, index }) => {
    return (
        <>
            {checkIfValidAccumulatorData(tierData, INDIVIDUAL_DEDUCTIBLE) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`${upperFirst(toLower(firstName))}'s ${toLower(
                                    tierData.tier == OUT_NETWORK_TIER
                                        ? 'out-of-network'
                                        : tierData.tier
                                )} deductible`}
                            </Typography.Paragraph>
                        </Grid>
                        {index == 0 && (
                            <Grid item sx={{ paddingTop: '0.19em', paddingLeft: '0.5em' }}>
                                <PopUpJSX props={toolTipDeductible} />
                            </Grid>
                        )}
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}IndividualDeductibleProgressBar`}
                        aria-label={`your${camelcase(
                            tierData.tier
                        )}IndividualDeductibleProgressBar`}
                        minValue={0}
                        maxValue={tierData?.deductible ? tierData.deductible.yearlyMax : 0}
                        actualValue={
                            tierData?.deductible
                                ? tierData.deductible.yearlyMax - tierData.deductible.remaining
                                : 0
                        }
                    />
                </>
            )}
            {checkIfValidAccumulatorData(tierData, INDIVIDUAL_OOP) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`${upperFirst(toLower(firstName))}'s ${toLower(
                                    tierData?.tier == OUT_NETWORK_TIER
                                        ? 'out-of-network'
                                        : tierData?.tier
                                )} out-of-pocket maximum`}
                            </Typography.Paragraph>
                        </Grid>
                        {index == 0 && (
                            <Grid item sx={{ paddingTop: '0.19em', paddingLeft: '0.5em' }}>
                                <PopUpJSX props={toolTipPocket} />
                            </Grid>
                        )}
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}IndividualOutOfPocketProgressBar`}
                        aria-label={`your${camelcase(
                            tierData.tier
                        )}IndividualOutOfPocketProgressBar`}
                        minValue={0}
                        maxValue={tierData?.oopMax ? tierData.oopMax.yearlyMax : 0}
                        actualValue={
                            tierData?.oopMax
                                ? tierData.oopMax.yearlyMax - tierData.oopMax.remaining
                                : 0
                        }
                    />
                </>
            )}
            {checkIfValidAccumulatorData(tierData, FAMILY_DEDUCTIBLE) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`Family ${toLower(
                                    tierData?.tier == OUT_NETWORK_TIER
                                        ? 'out-of-network'
                                        : tierData?.tier
                                )} deductible`}
                            </Typography.Paragraph>
                        </Grid>
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}FamilyDeductibleProgressBar`}
                        aria-label={`your${camelcase(tierData.tier)}FamilyDeductibleProgressBar`}
                        minValue={0}
                        maxValue={
                            tierData?.familyDeductible ? tierData.familyDeductible.yearlyMax : 0
                        }
                        actualValue={
                            tierData?.familyDeductible
                                ? tierData.familyDeductible.yearlyMax -
                                  tierData.familyDeductible.remaining
                                : 0
                        }
                    />
                </>
            )}
            {checkIfValidAccumulatorData(tierData, FAMILY_OOP) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`Family ${toLower(
                                    tierData?.tier == OUT_NETWORK_TIER
                                        ? 'out-of-network'
                                        : tierData?.tier
                                )} out-of-pocket maximum`}
                            </Typography.Paragraph>
                        </Grid>
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}FamilyOutOfPocketProgressBar`}
                        aria-label={`your${camelcase(tierData.tier)}FamilyOutOfPocketProgressBar`}
                        minValue={0}
                        maxValue={tierData?.familyOopMax ? tierData.familyOopMax.yearlyMax : 0}
                        actualValue={
                            tierData?.familyOopMax
                                ? tierData.familyOopMax.yearlyMax - tierData.familyOopMax.remaining
                                : 0
                        }
                    />
                </>
            )}
            {checkIfValidAccumulatorData(tierData, DENTAL_PLAN_MAX) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`Your annual maximum`}
                            </Typography.Paragraph>
                        </Grid>
                        {index == 0 && (
                            <Grid item sx={{ paddingTop: '0.19em', paddingLeft: '0.5em' }}>
                                <PopUpJSX props={toolTipDentalPlanMaximum} />
                            </Grid>
                        )}
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}dentalPlanMaxProgressBar`}
                        aria-label={`your${camelcase(tierData.tier)}dentalPlanMaximumProgressBar`}
                        minValue={0}
                        maxValue={tierData[DENTAL_PLAN_MAX].yearlyMax ?? 0}
                        actualValue={
                            tierData[DENTAL_PLAN_MAX].yearlyMax -
                                tierData[DENTAL_PLAN_MAX].remaining ?? 0
                        }
                    />
                </>
            )}
            {checkIfValidAccumulatorData(tierData, FAMILY_DENTAL_PLAN_MAX) && (
                <>
                    <Grid
                        container
                        style={{ marginBottom: 12, marginTop: '0.2em' }}
                        direction="row"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography.Paragraph style={{ fontWeight: 700, marginBottom: 0 }}>
                                {`Family annual maximum`}
                            </Typography.Paragraph>
                        </Grid>
                    </Grid>
                    <CostProgressBar
                        label={`your${camelcase(tierData.tier)}familyDentalPlanMaxProgressBar`}
                        aria-label={`your${camelcase(
                            tierData.tier
                        )}familyDentalPlanMaximumProgressBar`}
                        minValue={0}
                        maxValue={tierData[FAMILY_DENTAL_PLAN_MAX].yearlyMax ?? 0}
                        actualValue={
                            tierData[FAMILY_DENTAL_PLAN_MAX].yearlyMax -
                                tierData[FAMILY_DENTAL_PLAN_MAX].remaining ?? 0
                        }
                    />
                </>
            )}
        </>
    );
};
